import { ENV } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { useCallback, useEffect, useState } from 'react'

/**
 * Custom hook to handle Jira issue collector integration
 *
 * This hook loads the Jira issue collector script and provides
 * a function to trigger the feedback dialog with pre-filled user data
 *
 * @param customFieldValues - Optional additional field values to pre-fill in Jira
 */
export const useJiraFeedback = (customFieldValues?: Record<string, any>) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const { user } = useUserStore()

  const loadJiraCollector = useCallback(() => {
    // Check if jQuery is available
    if (typeof window !== 'undefined' && (window as any).jQuery) {
      const jQuery = (window as any).jQuery

      // Load the Jira collector script using environment variables
      jQuery.ajax({
        url: ENV.JIRA_COLLECTOR_URL,
        type: 'get',
        cache: true,
        dataType: 'script',
        success: () => {
          setIsLoaded(true)
        },
        error: () => {
          console.warn('Failed to load Jira collector script')
        },
      })

      // Set up the trigger function with user data pre-filling
      const fieldValues = {
        // Auto-fill fullname with user context (only if not provided in customFieldValues)
        ...(customFieldValues?.fullname ||
          (user?.firstName &&
            user?.lastName && {
              fullname: user.firstName + ' ' + user.lastName,
            })),
        // Auto-fill email field if user data is available (only if not provided in customFieldValues)
        ...(customFieldValues?.email ||
          (user?.email && {
            email: user.email,
          })),
        // Include any additional custom field values
        ...customFieldValues,
      }

      ;(window as any).ATL_JQ_PAGE_PROPS = {
        triggerFunction: function (showCollectorDialog: () => void) {
          // Create a custom trigger function that can be called programmatically
          ;(window as any).showJiraFeedbackDialog = showCollectorDialog
          setIsLoaded(true)
        },
        fieldValues,
        // Additional context about the user
        ...(user && {
          customFields: {
            // Add user role and tenant info if available
            ...(user.userRole && {
              'User Role': user.userRole,
            }),
            ...(user.tenant?.name && {
              Tenant: user.tenant.name,
            }),
            ...(user.tenant?.status && {
              'Tenant Status': user.tenant.status,
            }),
          },
        }),
      }
    } else {
      console.warn('jQuery is not available. Jira feedback will not work.')
    }
  }, [user, customFieldValues])

  const openFeedbackDialog = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).showJiraFeedbackDialog) {
      // Save current scroll position
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop

      // Find modal elements
      const modalWrapper = document.querySelector(
        '.flex.w-screen.fixed.inset-0.z-50',
      ) as HTMLElement
      const modalBackdrop = document.querySelector('.z-50.backdrop-blur-md') as HTMLElement

      // Hide modal to prevent focus conflicts with Jira dialog
      if (modalWrapper) {
        modalWrapper.style.display = 'none'
      }
      if (modalBackdrop) {
        modalBackdrop.style.display = 'none'
      }

      // Open Jira dialog
      ;(window as any).showJiraFeedbackDialog()

      // Restore scroll position after Jira dialog opens
      window.scrollTo(0, scrollPosition)

      // Restore modal immediately - this prevents focus conflicts
      // The modal stays hidden while Jira dialog is open, but gets restored
      // when the user interacts with the page after Jira dialog closes
      setTimeout(() => {
        if (modalWrapper) {
          modalWrapper.style.display = ''
        }
        if (modalBackdrop) {
          modalBackdrop.style.display = ''
        }
      }, 0)
    } else {
      console.warn('Jira feedback dialog not available. Make sure jQuery is loaded.')
      // Fallback: open a new window/tab to the Jira instance
      window.open(ENV.JIRA_SERVICE_DESK_URL, '_blank')
    }
  }, [])

  useEffect(() => {
    // Load the collector when the hook is initialized
    loadJiraCollector()
  }, [loadJiraCollector])

  return {
    openFeedbackDialog,
    isLoaded,
    hasUserData:
      !!(customFieldValues?.fullname || (user?.firstName && user?.lastName)) &&
      !!(customFieldValues?.email || user?.email),
  }
}
